import { convertSpacesToLatex, processBackslashes } from "@/utils/preprocess";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

// Declare the math-field element type
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'math-field': any;
    }
  }
}

const MATHFIELD_WRAPPER_ATTR = 'data-mathfield-wrapper';

export function installMathFieldWrapper() {
  const mathFields = document.querySelectorAll('math-field:not([data-inline])');
  for (const mathField of mathFields) {
    const shadowRoot = mathField.shadowRoot;
    if (shadowRoot) {
      // Skip if style already injected (idempotent)
      if (shadowRoot.querySelector(`style[${MATHFIELD_WRAPPER_ATTR}]`)) continue;
      const selector = shadowRoot.querySelector('.ML__latex');
      if (selector instanceof Element) {
        const ele = window.getComputedStyle(selector);
        if (ele.display && ele.display === 'block') {
          const style = document.createElement('style');
          style.setAttribute(MATHFIELD_WRAPPER_ATTR, '');
          style.textContent =
            ".ML__latex { \
                  width: 100% !important; \
                  display:flex !important;\
              }\
  \
              .ML__base {\
                  flex:1;\
                  display:flex !important;\
                  align-items: flex-end !important;\
                  flex-wrap:wrap;\
              }"
            ;
          shadowRoot.appendChild(style);
        }
      }
    }
  }
}

// split question text by \n for non-multiple choice questions
function splitQuestionText(questionText: string): { parts: string[], isDisplayMath: boolean } {
  if (!questionText) {
    return { parts: [], isDisplayMath: false };
  }

  // 检查是否包含 LaTeX 显示数学环境 \\[ ... \\]
  const hasDisplayMath = questionText.includes('\\\\[') && questionText.includes('\\\\]');

  if (hasDisplayMath) {
    // 如果包含显示数学环境，作为单个部分处理，但仍需要处理反斜杠
    const processedText = processBackslashes(questionText);
    const converted = convertSpacesToLatex(processedText.trim());
    return { parts: [converted], isDisplayMath: true };
  }

  // 普通情况：按 \n 分割成多个部分（不处理 \\）
  const splitParts = questionText.split(/\n/);

  const processedParts = splitParts
    .map((part) => {
      const trimmed = part.replace(/\s+$/, '');
      const processed = processBackslashes(trimmed);
      const converted = convertSpacesToLatex(processed);
      return converted;
    })
    .filter((part) => {
      // 过滤掉只包含 LaTeX 显示数学环境标记的部分
      const isOnlyBracket = part.trim() === '\\[' || part.trim() === '\\]';
      return part.length > 0 && !isOnlyBracket;
    });

  return { parts: processedParts, isDisplayMath: false };
}

interface ReadonlyQuestionDisplayProps {
  questionText: string;
  className?: string;
}

/**
 * 只读状态下非选择题题目的渲染组件
 * 将题目文本按照 \\ 或 \n 分割成多个 math-field 组件显示
 */
export function ReadonlyQuestionDisplay({
  questionText,
  className = "flex flex-col"
}: ReadonlyQuestionDisplayProps) {
  const { t } = useTranslation();

  const { parts } = splitQuestionText(questionText);

  // 在组件渲染后安装 math-field 样式
  useEffect(() => {
    installMathFieldWrapper();
  }, [questionText]);

  if (parts.length === 0) {
    return <div className={className}>{t("common.no-content")}</div>;
  }

  return (
    <div className={className}>
      {parts.map((textPart, index) => {
        return (
          <math-field
            key={`question-part-${index}`}
            read-only
            className="w-full"
            style={{
              backgroundColor: 'transparent',
              padding: 0,
              margin: 0,
              minHeight: '0',
              lineHeight: '1.4',
              display: 'block'
            }}
          >
            {textPart}
          </math-field>
        );
      })}
    </div>
  );
}
